import React from 'react';
import Modal from './modern/Modal';

export type DialogVariant = 'warning' | 'error' | 'success' | 'info';

export interface ConfirmDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Dialog variant - affects button colors
   */
  variant?: DialogVariant;

  /**
   * Dialog title
   */
  title: string;

  /**
   * Dialog message/description
   */
  message: string;

  /**
   * Confirm button text (default: "Confirm")
   */
  confirmText?: string;

  /**
   * Cancel button text (default: "Cancel")
   */
  cancelText?: string;

  /**
   * Callback when confirm is clicked
   */
  onConfirm: () => void;

  /**
   * Callback when cancel is clicked or dialog is closed
   */
  onCancel: () => void;
}

/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog based on Figma design
 * Supports different variants for different use cases
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   isOpen={showLogoutDialog}
 *   variant="error"
 *   title="Log Out?"
 *   message="You'll be signed out of your Hush account on this device."
 *   confirmText="Log Out"
 *   onConfirm={handleLogout}
 *   onCancel={() => setShowLogoutDialog(false)}
 * />
 * ```
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  variant = 'warning',
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}) => {
  // Get button color based on variant
  const getConfirmButtonStyle = () => {
    switch (variant) {
      case 'error':
        return 'bg-[#DC0000] hover:bg-[#B00000]';
      case 'warning':
        return 'bg-[#FF5210] hover:bg-[#E04800]';
      case 'success':
        return 'bg-[#458330] hover:bg-[#3A6F28]';
      case 'info':
        return 'bg-[#015FA6] hover:bg-[#014A85]';
      default:
        return 'bg-[#DC0000] hover:bg-[#B00000]';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      showCloseButton={false}
      maxWidth="max-w-[381px]"
    >
      <div className="text-center">
        {/* Title */}
        <h2
          className="font-manrope font-medium text-2xl text-[#0B111E] mb-6"
          style={{ lineHeight: '33px' }}
        >
          {title}
        </h2>

        {/* Message */}
        <p
          className="font-manrope font-light text-base text-[#0B111E] mb-8"
          style={{ lineHeight: '20px' }}
        >
          {message}
        </p>

        {/* Buttons */}
        <div className="flex items-center justify-center gap-4">
          {/* Cancel Button */}
          <button
            onClick={onCancel}
            className="flex items-center justify-center px-4 py-[7px] w-[134px] h-8 border border-[#015FA6] rounded-lg font-manrope font-medium text-sm text-[#015FA6] hover:bg-[#015FA6] hover:bg-opacity-5 transition-colors"
            style={{ lineHeight: '160%' }}
          >
            {cancelText}
          </button>

          {/* Confirm Button */}
          <button
            onClick={onConfirm}
            className={`flex items-center justify-center px-4 py-[7px] w-[134px] h-8 rounded-lg font-manrope font-medium text-sm text-white transition-colors ${getConfirmButtonStyle()}`}
            style={{ lineHeight: '160%' }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
